import { ActiveTrue } from '@shared/icons';
import { twMerge } from '@shared/themes';
import Image from 'next/image';
import type { ComponentProps } from 'react';
import { ContentGraphics } from '../../../ContentGraphics/ContentGraphics';
import { GraphicContainer } from '../../../GraphicContainer/GraphicContainer';

type DreamPodProps = ComponentProps<'div'> & {
  image: {
    src: string;
    alt: string;
  };
  title: string;
  description: string;
};

/**
 * DreamPod component - A card similar to PromotionPod with border, image with smile mask, and custom body content.
 * The body contains a title, ActiveTrue icon, and description.
 * Size is controlled via className (e.g., w-[109px] sm:w-[174px] md:w-[330px]).
 *
 * @param image - Image object with src and alt
 * @param title - Title text
 * @param description - Description text
 * @param className - Tailwind classes for sizing (width and height)
 */
function DreamPod({ className, image, title, description, ...props }: DreamPodProps) {
  const classes = {
    container: twMerge('rounded-md flex flex-col flex-shrink-0', className),
    imageWrapper: 'w-full flex relative isolate h-full',
    contentContainer: 'pt-1 md:pt-3 px-2 sm:px-3 pb-2 sm:pb-3 flex flex-col flex-1 bg-dream-pod-background',
    title: 'display-xxs-strong md:display-s-strong',
    iconDescription: 'flex items-center gap-1.5',
    description: 'paragraph-s sm:paragraph md:paragraph-l',
  };

  return (
    <ContentGraphics className={classes.container} data-testid="dream-pod" {...props}>
      <div className={classes.imageWrapper}>
        <Image src={image.src} alt={image.alt} fill className="object-cover absolute inset-0 size-full -z-10" />
        <GraphicContainer className="bg-transparent" maskClassName="fill-dream-pod-background h-2 md:h-3" />
      </div>
      <div className={classes.contentContainer}>
        <h4 className={classes.title} data-testid="dream-pod-title">
          {title}
        </h4>
        <div className={classes.iconDescription} data-testid="dream-pod-icon-description">
          <div className="flex-shrink-0 text-accent-green size-3 sm:size-4 md:size-5" data-testid="dream-pod-icon">
            <ActiveTrue className="size-full" />
          </div>
          <p className={classes.description} data-testid="dream-pod-description">
            {description}
          </p>
        </div>
      </div>
    </ContentGraphics>
  );
}

export { DreamPod, type DreamPodProps };
